/**
 * Country-name → flag-**emoji** helper for the team-selection picker rows, the
 * pipeline Table's Club column and the Similar Players list's nationality
 * segment.
 *
 * Name resolution is not done here — it belongs to the one shared resolver in
 * `@/utils/nationalityRegistry`, which every flag surface in the app now goes
 * through. This module only decides which *glyph* a resolved country wears,
 * and it differs from the registry in exactly one place: the four UK home
 * nations. They field separate national sides and the registry carries their
 * subdivision banners (🏴󠁧󠁢󠁥󠁮󠁧󠁿 and friends), but the product has always shown
 * them the Union Jack, so {@link SUBDIVISION_FLAG_OVERRIDES} keeps that.
 *
 * Until SE-6241 this file also carried a hand-curated 52-entry name → ISO map
 * that duplicated the registry. It was the older of the two lookup tables that
 * disagreed about "Türkiye" and "Korea, South"; every one of its entries is
 * reproduced exactly by the shared resolver (asserted in this module's tests),
 * so it is gone.
 * @module features/team-selection/utils/countryFlag
 */
import { resolveNationality } from "@/utils/nationalityRegistry";

/**
 * Registry codes whose flag the product deliberately renders as the Union
 * Jack rather than the subdivision banner the registry carries.
 * @remarks Keyed by registry `code`, not by name, so no second name-matching
 *   surface exists to drift from the shared resolver.
 */
const SUBDIVISION_FLAG_OVERRIDES: Readonly<Record<string, string>> = {
  "GB-ENG": "🇬🇧",
  "GB-SCT": "🇬🇧",
  "GB-WLS": "🇬🇧",
  "GB-NIR": "🇬🇧",
};

/**
 * Returns the flag emoji for a country name, or null when unknown.
 * @param country - Country name as returned by `Nationality.name`,
 *   `League.country` or `bioData.leagueCountry` (e.g. "Spain")
 * @returns The regional-indicator emoji (e.g. "🇪🇸"), or null when not mapped
 */
export const countryFlagEmoji = (
  country: string | null | undefined
): string | null => {
  const entry = resolveNationality(country);
  if (!entry) return null;

  return SUBDIVISION_FLAG_OVERRIDES[entry.code] ?? entry.emoji;
};

/**
 * Formats a country name for display: prepends a flag emoji when the
 * country resolves, otherwise returns just the country name.
 * @param country - Country name as returned by `League.country`
 * @returns "🇪🇸 Spain" when resolved, "Spain" when unresolved, null when blank
 */
export const formatCountrySegment = (
  country: string | null | undefined
): string | null => {
  if (!country) return null;
  const trimmed = country.trim();
  if (!trimmed) return null;
  const flag = countryFlagEmoji(trimmed);
  return flag ? `${flag} ${trimmed}` : trimmed;
};
